import type { EventMap, EventName } from './events';

/**
 * Lekki event bus do komunikacji Phaser ↔ React.
 *
 * **Type-safe:** `on()`, `emit()` i `off()` używają generics z constraint
 * `K extends EventName`, dzięki czemu:
 *  - Nazwa eventa musi istnieć w `EventMap` (literówki łapane przez kompilator)
 *  - Payload handler'a / argumentu jest auto-wywnioskowany z `EventMap[K]`
 *  - Brak ręcznego `eventBus.on<{...}>('foo', ...)` — typ pochodzi z mapy
 *
 * Przykład użycia:
 *   eventBus.emit('coins:changed', { newAmount: '120' });        // ✓ poprawne
 *   eventBus.emit('coins:changed', { wrongField: 120 });          // ✗ TS error
 *   eventBus.emit('nonexistent', {});                             // ✗ TS error
 *
 *   const off = eventBus.on('coins:changed', (e) => {
 *     e.newAmount;  // ← decimal string z EventMap['coins:changed']
 *   });
 *
 * Reguła: Phaser nie zna React i odwrotnie. Cała komunikacja przez named events.
 */

type Handler<T> = (payload: T) => void;

class EventBus {
  /** Map<eventName, Set<handler>>. `unknown` w insidach — type-safety enforced przez generic constraint na publicznych metodach. */
  private listeners = new Map<EventName, Set<Handler<unknown>>>();

  on<K extends EventName>(event: K, handler: Handler<EventMap[K]>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler as Handler<unknown>);
    return () => this.off(event, handler);
  }

  off<K extends EventName>(event: K, handler: Handler<EventMap[K]>): void {
    this.listeners.get(event)?.delete(handler as Handler<unknown>);
  }

  emit<K extends EventName>(event: K, payload: EventMap[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const h of set) {
      try {
        (h as Handler<EventMap[K]>)(payload);
      } catch (err) {
        console.error(`[EventBus] handler error on '${event}':`, err);
      }
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}

export const eventBus = new EventBus();
